import { createElement, type ReactNode } from 'react'
import { Languages, Loader2 } from 'lucide-react'
// Relative rather than Helm's @/ paths, so the API's tests can render it.
import type { TranslationContext } from '../../../lib/translation'
import { cn } from '../../../lib/utils'
import { useTranslated, type ObserveBlock, type TranslatedBlock } from './useTranslated'

/**
 * The control under a translatable block: "Translate", then "Translated from
 * Chinese · Show original". Nothing at all when the block is already in the
 * person's language. `label` names the block for a screen reader.
 */
export function TranslateToggle({
  block,
  label,
  className,
}: {
  block: TranslatedBlock
  label?: string
  className?: string
}) {
  if (!block.offered) return null
  const action = block.active ? 'Show original' : 'Translate'
  return (
    <span
      className={cn(
        'inline-flex flex-wrap items-center gap-x-1.5 gap-y-0.5 text-xs text-muted-foreground',
        className,
      )}
      data-translation={block.showing ? 'translated' : 'original'}
    >
      {block.showing && block.from && <span>{`Translated from ${block.from}`}</span>}
      {block.showing && block.from && <span aria-hidden>·</span>}
      <button
        type="button"
        onClick={block.toggle}
        aria-pressed={block.active}
        aria-label={label ? `${action} ${label}` : undefined}
        className="inline-flex items-center gap-1 rounded-sm font-medium text-primary underline-offset-2 outline-none hover:underline focus-visible:ring-2 focus-visible:ring-ring"
      >
        {block.pending ? (
          <Loader2 aria-hidden className="size-3 animate-spin" />
        ) : (
          <Languages aria-hidden className="size-3" />
        )}
        {block.pending ? 'Translating…' : action}
      </button>
      {block.error && (
        <span role="alert" className="text-destructive">
          {block.error}{' '}
          <button
            type="button"
            onClick={block.retry}
            className="font-medium underline underline-offset-2"
          >
            Try again
          </button>
        </span>
      )}
    </span>
  )
}

/**
 * One text that can be read in the person's language, in place: the record's
 * text, then the control. Adding a block is one line:
 * `<Translatable text={note} context="note" />`.
 */
export function Translatable({
  text,
  context,
  as = 'p',
  className,
  label,
}: {
  text: string | null | undefined
  context: TranslationContext
  as?: 'p' | 'div' | 'span'
  className?: string
  label?: string
}) {
  const [observe, block] = useTranslated([text], context)
  if (!text) return null
  return (
    <>
      {createElement(as, { ref: observe, className }, block.text(0))}
      {block.offered && (
        <div className="mt-1">
          <TranslateToggle block={block} label={label} />
        </div>
      )}
    </>
  )
}

/**
 * Several texts read as one block (a courier's scans, an item's name and
 * description, a message's subject and body), drawn by the caller: it gets
 * the block and a callback ref for the block's element, `block.text(i)` where each text
 * goes, and the toggle where it fits.
 */
export function Translated({
  texts,
  context,
  children,
}: {
  texts: ReadonlyArray<string | null | undefined>
  context: TranslationContext
  children: (block: TranslatedBlock, observe: ObserveBlock) => ReactNode
}) {
  const [observe, block] = useTranslated(texts, context)
  return <>{children(block, observe)}</>
}

/**
 * The translation of a text that is being edited, under it: the field keeps
 * the original, which is what is saved.
 */
export function TranslationBelow({
  text,
  context,
  label,
}: {
  text: string | null | undefined
  context: TranslationContext
  label?: string
}) {
  const [observe, block] = useTranslated([text], context)
  if (!block.offered) return null
  return (
    <div ref={observe} className="space-y-1">
      <TranslateToggle block={block} label={label} />
      {block.showing && (
        <p className="whitespace-pre-wrap break-words rounded-md bg-muted/60 px-3 py-2 text-sm">
          {block.text(0)}
        </p>
      )}
    </div>
  )
}

/** A message as records show it, its subject (when it has one) over its body, translatable as one. */
export function TranslatableMessage({
  subject,
  body,
  bodyClassName = 'whitespace-pre-wrap text-sm',
}: {
  subject?: string | null
  body: string | null | undefined
  bodyClassName?: string
}) {
  const [observe, block] = useTranslated([subject, body], 'message')
  return (
    <div ref={observe} className="space-y-2">
      {subject && <div className="font-medium">{block.text(0)}</div>}
      {body && <p className={bodyClassName}>{block.text(1)}</p>}
      <TranslateToggle block={block} label="this message" />
    </div>
  )
}
